import React, { useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { resendVerificationEmail } from '../services/authService';

const UnprovisionedView: React.FC = () => {
  const { logout, user, awaitingEmailVerification } = useAuthContext();
  const [resendState, setResendState] = useState<'idle' | 'sent' | 'error'>('idle');

  const handleResend = async () => {
    try {
      await resendVerificationEmail();
      setResendState('sent');
    } catch {
      setResendState('error');
    }
  };

  // A provision is waiting but can't attach until the email is verified —
  // say so, instead of the generic "ask your administrator" dead end.
  if (awaitingEmailVerification) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-white px-6 text-center">
        <div className="w-16 h-16 bg-status-warn-bg rounded-full flex items-center justify-center mb-6">
          <svg className="w-8 h-8 text-status-warn" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-text-ink mb-2">Verify your email</h1>
        <p className="text-text-muted max-w-sm mb-8 leading-relaxed">
          We sent a verification link to <strong>{user?.email}</strong>.
          Click it, then come back here and continue.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2.5 bg-action text-white font-bold rounded-full text-sm hover:bg-action-dark transition-colors mb-3"
        >
          I&rsquo;ve verified — continue
        </button>
        <button
          onClick={handleResend}
          disabled={resendState === 'sent'}
          className="px-6 py-2.5 text-text-muted font-medium text-sm hover:text-text-ink transition-colors mb-3"
        >
          {resendState === 'sent' ? 'Sent — check your inbox'
            : resendState === 'error' ? 'Could not send — try again'
            : 'Resend the email'}
        </button>
        <button
          onClick={logout}
          className="px-6 py-2.5 text-text-faint font-medium text-sm hover:text-text-ink transition-colors"
        >
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-white px-6 text-center">
      <div className="w-16 h-16 bg-status-warn-bg rounded-full flex items-center justify-center mb-6">
        <svg className="w-8 h-8 text-status-warn" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-text-ink mb-2">Account Not Set Up</h1>
      <p className="text-text-muted max-w-sm mb-8 leading-relaxed">
        Your account hasn't been provisioned yet. Please use an invitation link to join a study, or ask your administrator to set up your account.
      </p>
      <button
        onClick={logout}
        className="px-6 py-2.5 bg-action text-white font-bold rounded-full text-sm hover:bg-action-dark transition-colors"
      >
        Sign Out
      </button>
    </div>
  );
};

export default UnprovisionedView;
